/**
 * The bus structure everything else plays into.
 *
 *   sfx ----\
 *   engine --+--> master(volume) --> limiter --> output(mute) --> destination
 *   ui -----/
 *
 * Three properties this file exists to guarantee:
 *
 *   1. NOTHING happens at import time. Browsers refuse to start an AudioContext
 *      before a user gesture, and Node has no AudioContext at all, so
 *      constructing one eagerly either logs a console warning on every page
 *      load or throws inside a test runner. The graph is built on the first
 *      `ensure()` and every accessor returns null until then.
 *
 *   2. A suspended context is a normal state, not an error. The game calls
 *      `resume()` from the first input event; until that resolves, sounds are
 *      scheduled into a context that is not advancing, which is harmless.
 *
 *   3. Mute and volume are separate nodes. Muting by zeroing the master gain
 *      loses the user's volume setting, so mute lives on its own node after
 *      the limiter and both ramp rather than jump — a gain step of 1.0 in one
 *      sample block is an audible click.
 */

export type BusName = 'sfx' | 'engine' | 'ui';

export interface Buses {
  ctx: AudioContext;
  /** Post-fader, pre-limiter. Sub-buses connect here. */
  master: GainNode;
  limiter: DynamicsCompressorNode;
  /** Mute stage, after the limiter. */
  output: GainNode;
  sfx: GainNode;
  engine: GainNode;
  ui: GainNode;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

/**
 * Locate the constructor without touching an undefined global. Safari still
 * only has the prefixed name, and Node has neither — in which case every
 * public method on Mixer degrades to a no-op rather than throwing.
 */
const findAudioContextCtor = (): AudioContextCtor | null => {
  const g = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
};

/** True when this environment can play audio at all. */
export const isAudioSupported = (): boolean => findAudioContextCtor() !== null;

const DEFAULT_BUS_GAIN: Record<BusName, number> = {
  // The engine is a continuous bed and everything else has to sit on top of it,
  // so it starts well below the one-shots.
  engine: 0.45,
  sfx: 0.9,
  ui: 0.7,
};

/** Seconds over which volume and mute changes are ramped. */
const FADE = 0.04;

export class Mixer {
  private buses: Buses | null = null;
  private failed = false;

  private masterVolume = 0.9;
  private busVolume: Record<BusName, number> = { ...DEFAULT_BUS_GAIN };
  private muted = false;

  /** The graph, built on first use. Null when Web Audio is unavailable. */
  ensure(): Buses | null {
    if (this.buses) return this.buses;
    if (this.failed) return null;

    const Ctor = findAudioContextCtor();
    if (!Ctor) {
      this.failed = true;
      return null;
    }

    let ctx: AudioContext;
    try {
      ctx = new Ctor({ latencyHint: 'interactive' });
    } catch {
      // Some embedders throw on construction rather than returning a suspended
      // context. Give up permanently instead of retrying every frame.
      this.failed = true;
      return null;
    }

    const master = ctx.createGain();
    master.gain.value = this.masterVolume;

    // Web Audio has no true limiter, so this is a compressor set hard enough to
    // act as one: no knee, high ratio, fast attack. Its job is not tone, it is
    // stopping a six-car pinch from clipping the output.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.25;

    const output = ctx.createGain();
    output.gain.value = this.muted ? 0 : 1;

    master.connect(limiter);
    limiter.connect(output);
    output.connect(ctx.destination);

    const makeBus = (name: BusName): GainNode => {
      const g = ctx.createGain();
      g.gain.value = this.busVolume[name];
      g.connect(master);
      return g;
    };

    this.buses = {
      ctx,
      master,
      limiter,
      output,
      sfx: makeBus('sfx'),
      engine: makeBus('engine'),
      ui: makeBus('ui'),
    };

    // Opportunistic: if we happen to be inside a gesture already this starts
    // the clock immediately, and if not the rejection is expected and ignored.
    void this.resume();

    return this.buses;
  }

  get context(): AudioContext | null {
    return this.buses?.ctx ?? null;
  }

  /** Context clock, or 0 before the graph exists. Safe to schedule against. */
  get now(): number {
    return this.buses?.ctx.currentTime ?? 0;
  }

  get sampleRate(): number {
    return this.buses?.ctx.sampleRate ?? 48000;
  }

  get state(): AudioContextState | 'unavailable' {
    return this.buses?.ctx.state ?? 'unavailable';
  }

  bus(name: BusName): GainNode | null {
    return this.ensure()?.[name] ?? null;
  }

  /**
   * Call from the first user gesture (and again on visibility changes — mobile
   * browsers suspend the context on backgrounding). Resolves to whether the
   * context is actually running afterwards.
   */
  async resume(): Promise<boolean> {
    const b = this.ensure();
    if (!b) return false;
    if (b.ctx.state === 'running') return true;
    try {
      await b.ctx.resume();
    } catch {
      return false;
    }
    // Re-read through a widened type: the compiler narrowed `state` above and
    // has no way to know that awaiting resume() can change it.
    return (b.ctx.state as AudioContextState) === 'running';
  }

  async suspend(): Promise<void> {
    const b = this.buses;
    if (!b || b.ctx.state !== 'running') return;
    try {
      await b.ctx.suspend();
    } catch {
      // Suspension is a courtesy; failing to do it is not worth reporting.
    }
  }

  private ramp(param: AudioParam | undefined, value: number): void {
    if (!param) return;
    const t = this.now;
    // cancelAndHold is not in Safari; cancelScheduledValues plus an explicit
    // setValueAtTime of the current value is the portable equivalent.
    param.cancelScheduledValues(t);
    param.setValueAtTime(param.value, t);
    param.linearRampToValueAtTime(value, t + FADE);
  }

  setMasterVolume(v: number): void {
    this.masterVolume = Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : this.masterVolume;
    this.ramp(this.buses?.master.gain, this.masterVolume);
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  setBusVolume(name: BusName, v: number): void {
    if (!Number.isFinite(v)) return;
    this.busVolume[name] = Math.max(0, Math.min(1, v));
    this.ramp(this.buses?.[name].gain, this.busVolume[name]);
  }

  getBusVolume(name: BusName): number {
    return this.busVolume[name];
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.ramp(this.buses?.output.gain, muted ? 0 : 1);
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Tear the graph down. Volume settings survive so a later `ensure()` rebuilds
   * the same mix — this is for releasing the hardware, not for resetting state.
   */
  dispose(): void {
    const b = this.buses;
    this.buses = null;
    if (!b) return;
    try {
      b.output.disconnect();
      b.limiter.disconnect();
      b.master.disconnect();
      b.sfx.disconnect();
      b.engine.disconnect();
      b.ui.disconnect();
      void b.ctx.close();
    } catch {
      // Already closed by the browser, e.g. on page teardown.
    }
  }
}

// A process-wide mixer, created on first request rather than at import.
let shared: Mixer | null = null;

export const sharedMixer = (): Mixer => (shared ??= new Mixer());

export const disposeSharedMixer = (): void => {
  shared?.dispose();
  shared = null;
};
